import React, { useEffect, useState, useMemo } from 'react';
import { Box, Rating, Typography, TextField, IconButton, DialogContent, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import text from '../../lib/dictionary.json';
import Spinner from '../reusable/Spinner';
import { get } from '../../services/http';
import { RateIcon } from '../../Icons';
import Separator from '../Separator';

const ratingLabels = { 1: 'poor', 2: 'fair', 3: 'good', 4: 'verygood', 5: 'excellent' };

const formatDuration = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '---';
  
  const totalHours = parseFloat(value);
  const hours = Math.floor(totalHours);
  const minutes = Math.round((totalHours - hours) * 60);

  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');

  return `${hh}h and ${mm}m`;
};


const Review = ({ open, theme, lang, setAlertState, id, type, columns, onClose, onCancel, onSubmit }) => {
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(true);
    const [row, setRow] = useState({});

    const commonStyles = {
        font: 'Roboto, sans-serif',
        grayText: '#676E7A',
        darkText: '#3E4148',
        lightGray: '#89909E'
    };

    useEffect(() => {
        if (type && id && open) {
            setLoading(true);
            get(`/${type}/get/${id}`)
                .then(res => setRow(res.data))
                .catch(err => setAlertState({ 
                    open: true, 
                    message: err.response?.data?.message ?? err.message, 
                    type: 'error' 
                }))
                .finally(() => setLoading(false));
        }
    }, [type, id, open, setAlertState]);

    const clearAndClose = () => {
        setRating(5);
        setComment('');
        onClose();
    };

    const handleSubmit = () => {
        onSubmit(rating, comment);
        clearAndClose();
    };

    const handleCancelAction = () => {
        onCancel();
        clearAndClose();
    };

    return (
        <div id='modal' style={{ fontFamily: commonStyles.font }}>
            <div id='popup' onClick={(e) => e.stopPropagation()} style={{ width: '45rem' }}>
                <Box sx={{
                    height:'100%',
                    width: '100%',
                    background: `radial-gradient(120% 80% at 50% 0%, var(--app-gradient-1) 0, var(--app-gradient-2) 30%, var(--root-background-color) 60%)`,
                }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '1.5rem', height: '6rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Box sx={{ width: '3.125rem', height: '3.125rem', background: 'rgba(223,220,3,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <RateIcon />
                            </Box>
                            <Typography variant="h4" sx={{ fontSize: 'var(--large)', fontWeight: 'bold', color: '#111827' }}>
                                {text[lang]['review-title']}
                            </Typography>
                        </Box>
                        {!loading && (
                            <IconButton onClick={handleCancelAction} sx={{ ':hover': { backgroundColor: theme === 'light-theme' ? 'rgb(190, 193, 200)' : 'rgb(60, 66, 80)' } }}>
                                <CloseIcon sx={{ fill: theme === 'light-theme' ? 'rgb(23, 26, 33)' : 'rgb(199, 202, 209)' }} />
                            </IconButton>
                        )}
                    </Box>

                    <Separator />

                    {loading ? <Spinner /> : (
                        <DialogContent sx={{ scrollbarWidth: 'thin', overflowY: 'auto', height: 'calc(100% - 6rem)', p: '1.5rem' }}>
                            
                            {/* Question Section */}
                            <Box sx={{ textAlign: 'center', mb: 2 }}>
                                <Typography component="div" sx={{ fontSize: '1.5rem', fontWeight: 600, color: commonStyles.grayText }}>
                                    Do you like our service in{" "}
                                    <Box component="span" sx={{ color: commonStyles.darkText, textTransform: 'capitalize' }}>
                                        {type} ID: {id} ?
                                    </Box>
                                </Typography>
                                <Typography sx={{ fontSize: '1.125rem', color: commonStyles.lightGray, mt: 1 }}>
                                    Give us a quick rating so we know if you like it.
                                </Typography>
                            </Box>

                            {/* Data Grid Box */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3 }}>
                                <Box sx={{
                                    p: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, width: '30.125rem',
                                    border: '2px solid', borderImage: 'linear-gradient(to right, rgba(30, 68, 108, 0.5), rgba(76, 105, 214, 0.2)) 1',
                                    borderRadius: '0.625rem'
                                }}>
                                    {columns.map(col => (
                                        <Box key={col} sx={{ textAlign: 'start' }}>
                                            <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                                                {text[lang][`add-${type}-${col}-name`]?.replace('*', '')?.trim()}:
                                            </Typography>
                                            <Typography sx={{ fontSize: '1.125rem', color: '#374151', fontWeight: 500 }}>
                                                {col === 'duration' ? formatDuration(row[col]) : (row[col] || '---')}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>

                                {/* Rating Section */}
                                <Typography sx={{ fontSize: '1.5rem', mt: 4, color: commonStyles.grayText, fontWeight: 600 }}>
                                    {text[lang]['review-question']}
                                </Typography>
                                <Rating
                                    value={rating}
                                    onChange={(_, val) => setRating(val)}
                                    icon={<StarIcon fontSize="large" sx={{ fill: '#E5E209' }} />}
                                    emptyIcon={<StarIcon fontSize="large" sx={{ fill: theme === 'light-theme' ? '#BEC1C8' : '#3C4250' }} />}
                                    sx={{ my: 1 }}
                                />
                                {rating > 0 && (
                                    <Typography sx={{ color: commonStyles.grayText }}>
                                        {text[lang][`review-label-${ratingLabels[rating]}`]}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ my: 3 }}><Separator /></Box>

                            {/* Comment Section */}
                            <Typography sx={{ textAlign: lang === 'en' ? 'left' : 'right', fontWeight: 'bold', mb: 1, color: '#374151' }}>
                                {text[lang]['review-comment']}
                            </Typography>
                            <TextField
                                multiline
                                rows={4}
                                fullWidth
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme === 'light-theme' ? '#171A21' : '#C7CAD1',
                                        '& fieldset': { borderColor: theme === 'light-theme' ? '#5B6165' : '#82888C' },
                                        '&:hover fieldset': { borderColor: '#295EAC' },
                                        '&.Mui-focused fieldset': { borderColor: '#295EAC' }
                                    }
                                }}
                            />

                            {/* Actions */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                                <Button onClick={handleCancelAction} sx={{ color: '#111827', textTransform: 'capitalize', width: '8rem' }}>
                                    {text[lang]['cancel']}
                                </Button>
                                <Button 
                                    onClick={handleSubmit} 
                                    disabled={!rating} 
                                    variant="contained"
                                    sx={{ backgroundColor: '#16324F', color: 'white', textTransform: 'capitalize', width: '8rem', ':hover': { backgroundColor: '#10253A' } }}
                                >
                                    {text[lang]['save']}
                                </Button>
                            </Box>
                        </DialogContent>
                    )}
                </Box>
            </div>
        </div>
    );
};

export default Review;